import { useState } from "react";
import {
    Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, List, ListItem, ListItemAvatar,
    ListItemText, Paper, Skeleton, TextField, Tooltip, Typography,
} from "@mui/material";
import { PersonAdd, PersonRemove } from "@mui/icons-material";
import { ConfirmacaoAcaoModal } from "../../../../../core/components/ConfirmacaoAcaoModal";
import { useDataHora } from "../../../../../core/hooks/useDataHora";
import { IMembroOrganizacao } from "../../../../interfaces/IOrganizacao";
import { VazioBox } from "../../styles";
import { iniciais } from "../../utils";

interface IAbaUsuariosProps {
    membros: IMembroOrganizacao[];
    usuarioAtualUuid?: string;
    carregando: boolean;
    salvando: boolean;
    onAdicionar: (email: string) => Promise<boolean>;
    onRemover: (usuarioUuid: string) => Promise<void>;
}

export const AbaUsuarios = ({ membros, usuarioAtualUuid, carregando, salvando, onAdicionar, onRemover }: IAbaUsuariosProps) => {
    const { dataBr } = useDataHora();

    const [email, setEmail] = useState("");
    const [removendo, setRemovendo] = useState<IMembroOrganizacao>();

    const handleAdicionar = async () => {
        if (await onAdicionar(email.trim())) {
            setEmail("");
        }
    };

    return (
        <Box sx={{ mt: 2, maxWidth: 760 }}>
            <Alert severity="info" sx={{ borderRadius: 3, mb: 2 }}>
                Todo membro da organização enxerga e administra <strong>todos</strong> os estabelecimentos dela —
                incluindo assinaturas e bloqueios de acesso.
            </Alert>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="h6">Adicionar membro</Typography>
                <Typography variant="body2" color="text.secondary">
                    O usuário já precisa ter conta na Space Food. Informe o e-mail do cadastro dele.
                </Typography>
                <Box
                    component="form"
                    onSubmit={(evento: React.FormEvent) => { evento.preventDefault(); handleAdicionar(); }}
                    sx={{ display: "flex", gap: 1, mt: 2 }}
                >
                    <TextField
                        label="E-mail do usuário"
                        type="email"
                        size="small"
                        fullWidth
                        value={email}
                        onChange={(evento) => setEmail(evento.target.value)}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<PersonAdd />}
                        disabled={salvando || !email.trim()}
                        sx={{ whiteSpace: "nowrap" }}
                    >
                        Adicionar
                    </Button>
                </Box>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6">Membros</Typography>
                    <Chip size="small" label={membros.length} color="primary" />
                    {salvando && <CircularProgress size={16} sx={{ ml: "auto" }} />}
                </Box>
                <Divider sx={{ my: 1.5 }} />

                {carregando ? (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        {Array.from({ length: 3 }, (_, indice) => (
                            <Skeleton key={indice} variant="rounded" height={56} sx={{ borderRadius: 2 }} />
                        ))}
                    </Box>
                ) : !membros.length ? (
                    <VazioBox>
                        <Typography variant="body2" color="text.secondary">
                            Nenhum membro cadastrado ainda.
                        </Typography>
                    </VazioBox>
                ) : (
                    <List disablePadding>
                        {membros.map(membro => {
                            const ehVoce = membro.usuarioUuid === usuarioAtualUuid;

                            return (
                                <ListItem
                                    key={membro.usuarioUuid}
                                    disableGutters
                                    secondaryAction={ehVoce ? (
                                        <Chip size="small" label="Você" />
                                    ) : (
                                        <Tooltip title="Remover da organização">
                                            <IconButton edge="end" size="small" color="error" onClick={() => setRemovendo(membro)}>
                                                <PersonRemove fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: "primary.main", width: 40, height: 40, fontSize: "0.9rem" }}>
                                            {iniciais(membro.nome ?? membro.email)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={membro.nome ?? membro.email ?? "Usuário"}
                                        secondary={[
                                            membro.email,
                                            membro.telefone,
                                            membro.cadastradoAs && `Desde ${dataBr(membro.cadastradoAs)}`,
                                        ].filter(Boolean).join(" · ")}
                                        primaryTypographyProps={{ fontWeight: 600 }}
                                        secondaryTypographyProps={{ variant: "caption" }}
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Paper>

            <ConfirmacaoAcaoModal
                open={!!removendo}
                titulo="Remover membro?"
                descricao={`${removendo?.nome ?? removendo?.email ?? "O usuário"} perde o acesso a todos os estabelecimentos da organização. Os acessos diretos que ele tenha a um estabelecimento continuam valendo.`}
                loading={salvando}
                onCancelar={() => setRemovendo(undefined)}
                onConfirmar={async () => {
                    if (removendo) {
                        await onRemover(removendo.usuarioUuid);
                    }
                    setRemovendo(undefined);
                }}
            />
        </Box>
    );
};
